// pages/TimelinePage.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
  Home,
  List,
  Kanban,
  GanttChart,
  LayoutDashboard,
  Calendar,
  MessageSquare,
  Paperclip,
  Star,
  ChevronDown as ChevronDownIcon,
  MoreVertical,
  Edit,
  Trash2,
  Plus,
  ChevronLeft,
  ChevronRight,
  Filter,
  ArrowUpDown,
  X,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  UserCircle,
  GripVertical,
  Clock,
  Lock,
  Link as LinkIcon,
  Tag,
  Search,
  Settings,
  Grid3x3,
  Vote as VoteIcon,
  FileText
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { RootState } from '../store/store';
import { selectUser } from '../store/slices/authSlice';
import { API_BASE_URL } from '../config/api';
import ProjectNavbar from '../components/ProjectNavbar';

interface ProjectData {
  _id: string;
  title: string;
  description: string;
  start: string;
  end: string;
  type: 'meeting' | 'workshop' | 'deadline' | 'social' | 'project';
  isPublic: boolean;
  color: string;
  creator: { _id: string; name: string; email: string };
  teams?: any[];
}

interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  deadline?: string;
  startDate?: string;
  duration?: number; // Duration in days
  assignee?: { _id: string; name: string; email: string };
  team?: { _id: string; name: string };
}

const TimelinePage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => selectUser(state));
  const API_BASE = API_BASE_URL;

  const [project, setProject] = useState<ProjectData | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoomLevel, setZoomLevel] = useState<'week' | 'month' | 'quarter'>('week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sections, setSections] = useState<{ id: string; name: string; status?: string; collapsed: boolean }[]>([
    { id: 'todo', name: 'To do', status: 'todo', collapsed: false },
    { id: 'doing', name: 'Doing', status: 'in-progress', collapsed: false },
    { id: 'review', name: 'Review', status: 'review', collapsed: false },
    { id: 'blocked', name: 'Blocked', status: 'blocked', collapsed: false },
    { id: 'done', name: 'Done', status: 'done', collapsed: false },
    { id: 'submitted', name: 'Submitted', status: 'submitted', collapsed: false },
    { id: 'under-review', name: 'Under Review', status: 'under-review', collapsed: false },
    { id: 'revision-requested', name: 'Revision Requested', status: 'revision-requested', collapsed: false },
    { id: 'rejected', name: 'Rejected', status: 'rejected', collapsed: false }
  ]);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showEditTaskModal, setShowEditTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const [draggedDate, setDraggedDate] = useState<string | null>(null);
  const [taskFormData, setTaskFormData] = useState({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    deadline: '',
    startDate: '',
    duration: 1,
    assignee: '',
    team: ''
  });
  const [editTaskData, setEditTaskData] = useState({
    duration: 1,
    startDate: '',
    deadline: ''
  });
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filters, setFilters] = useState({
    priorities: [] as string[],
    assignees: [] as string[],
    statuses: [] as string[],
    hasDate: null as boolean | null,
  });

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  // Fetch project and tasks
  useEffect(() => {
    if (projectId && user?._id) {
      fetchProject();
      fetchTasks();
    }
  }, [projectId, user?._id]);

  // Auto-scroll to first task or today after tasks are loaded
  useEffect(() => {
    if (tasks.length > 0 && !loading) {
      // Wait for timeline to render, then scroll to center of tasks
      setTimeout(() => {
        const tasksWithDates = tasks.filter(t => t.startDate || t.deadline);
        if (tasksWithDates.length > 0) {
          // Find the earliest task date
          const earliestTask = tasksWithDates.reduce((earliest, task) => {
            const taskDate = task.startDate ? new Date(task.startDate) : new Date(task.deadline!);
            const earliestDate = earliest.startDate ? new Date(earliest.startDate) : new Date(earliest.deadline!);
            return taskDate < earliestDate ? task : earliest;
          });
          
          const taskDate = earliestTask.startDate ? new Date(earliestTask.startDate) : new Date(earliestTask.deadline!);
          const today = new Date();
          const scrollToDate = taskDate < today ? taskDate : today;
          
          // Scroll to the date
          const timelineContainer = document.querySelector('.timeline-container');
          if (timelineContainer) {
            const { start } = getTimeRange();
            const daysDiff = Math.floor((scrollToDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
            const weekWidth = zoomLevel === 'quarter' ? 60 : zoomLevel === 'month' ? 80 : 100;
            const scrollPosition = (daysDiff / 7) * weekWidth - (timelineContainer.clientWidth / 2);
            timelineContainer.scrollTo({ left: Math.max(0, scrollPosition), behavior: 'smooth' });
          }
        } else {
          // No tasks with dates, scroll to today
          goToToday();
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks.length, loading, zoomLevel]);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API_BASE}/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProject(res.data.data);
    } catch (err: any) {
      console.error('Erreur chargement projet:', err);
      setError(err.response?.data?.error || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    if (!projectId || !user?._id) return;
    try {
      const res = await axios.get(`${API_BASE}/tasks/${projectId}?userId=${user._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const responseData = res.data?.data;
      let fetchedTasks: Task[] = [];
      if (responseData && Array.isArray(responseData.tasks)) {
        fetchedTasks = responseData.tasks;
      } else if (Array.isArray(responseData)) {
        fetchedTasks = responseData;
      }
      
      console.log('Fetched tasks:', fetchedTasks.length);
      console.log('Tasks with dates:', fetchedTasks.filter(t => t.startDate || t.deadline).length);
      fetchedTasks.forEach(task => {
        console.log(`Task: ${task.title}`, {
          status: task.status,
          startDate: task.startDate,
          deadline: task.deadline,
          duration: task.duration
        });
      });
      
      setTasks(fetchedTasks);
    } catch (err: any) {
      console.error('Erreur chargement tâches:', err);
      setTasks([]);
    }
  };

  const isCreator = project?.creator?._id === user?._id;

  // Navigation functions
  // Scroll to today in the timeline
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    // Scroll to today indicator after a brief delay
    setTimeout(() => {
      const todayIndicator = document.querySelector('.today-indicator');
      if (todayIndicator) {
        todayIndicator.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 100);
  };
  
  // Navigation functions - now used for scrolling/centering view
  const goToPrevious = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };
  
  const goToNext = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const zoomIn = () => {
    if (zoomLevel === 'quarter') setZoomLevel('month');
    else if (zoomLevel === 'month') setZoomLevel('week');
  };

  const zoomOut = () => {
    if (zoomLevel === 'week') setZoomLevel('month');
    else if (zoomLevel === 'month') setZoomLevel('quarter');
  };

  // Calculate weeks/months to display - include past and future dates
  const getTimeRange = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    // Find the earliest and latest task dates
    let minTaskDate: Date | null = null;
    let maxTaskDate: Date | null = null;
    
    tasks.forEach(task => {
      if (task.startDate) {
        const startDate = new Date(task.startDate);
        if (isNaN(startDate.getTime())) {
          console.warn('Invalid startDate for task:', task.title, task.startDate);
          return;
        }
        
        // Calculate end date based on duration
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + (task.duration || 1) - 1);
        
        // Track earliest start date
        if (minTaskDate === null || startDate.getTime() < minTaskDate.getTime()) {
          minTaskDate = new Date(startDate);
        }
        
        // Track latest end date
        if (maxTaskDate === null || endDate.getTime() > maxTaskDate.getTime()) {
          maxTaskDate = new Date(endDate);
        }
      } else if (task.deadline) {
        const deadline = new Date(task.deadline);
        if (isNaN(deadline.getTime())) {
          console.warn('Invalid deadline for task:', task.title, task.deadline);
          return;
        }
        
        // Track earliest deadline
        if (minTaskDate === null || deadline.getTime() < minTaskDate.getTime()) {
          minTaskDate = new Date(deadline);
        }
        
        // Track latest deadline
        if (maxTaskDate === null || deadline.getTime() > maxTaskDate.getTime()) {
          maxTaskDate = new Date(deadline);
        }
      }
    });
    
    // Calculate start date: use earliest task date or start from 3 months before today
    let start: Date;
    if (minTaskDate !== null) {
      // Start from the beginning of the month containing the earliest task, minus 1 month padding
      start = new Date(minTaskDate);
      start.setDate(1); // Start of month
      start.setMonth(start.getMonth() - 1); // Add 1 month padding before
    } else {
      // No tasks with dates, start from 3 months before today
      start = new Date(today);
      start.setMonth(start.getMonth() - 3);
      start.setDate(1); // Start of month
    }
    
    // Calculate end date: either based on tasks or default to 3 months ahead
    let end: Date;
    if (maxTaskDate !== null) {
      const taskDate = maxTaskDate as Date;
      // Add 1 month padding after the latest task
      end = new Date(taskDate);
      end.setMonth(end.getMonth() + 1);
      end.setDate(0); // Last day of that month
    } else {
      // Default: show 3 months ahead from today
      end = new Date(today);
      end.setMonth(end.getMonth() + 3);
      end.setDate(0); // Last day of that month
    }
    
    // Ensure we show at least 3 months from today if no tasks
    const minEnd = new Date(today);
    minEnd.setMonth(minEnd.getMonth() + 3);
    minEnd.setDate(0);
    if (end < minEnd) {
      end = minEnd;
    }
    
    // Ensure we show at least from today if no past tasks
    const minStart = new Date(today);
    minStart.setDate(1); // Start of current month
    if (start > minStart) {
      start = minStart;
    }
    
    // Create typed variables for logging with explicit type assertions
    const minTaskDateStr: string | null = minTaskDate !== null ? (minTaskDate as Date).toISOString() : null;
    const maxTaskDateStr: string | null = maxTaskDate !== null ? (maxTaskDate as Date).toISOString() : null;
    
    console.log('Timeline date range:', { 
      start: start.toISOString(), 
      end: end.toISOString(),
      today: today.toISOString(),
      taskCount: tasks.length,
      minTaskDate: minTaskDateStr,
      maxTaskDate: maxTaskDateStr
    });
    
    return { start, end };
  };

  // Get tasks without date
  const tasksWithoutDate = tasks.filter(task => !task.deadline && !task.startDate);

  // Apply filters to tasks
  const getFilteredTasks = useCallback(() => {
    let filtered = [...tasks];

    // Filter by priorities
    if (filters.priorities.length > 0) {
      filtered = filtered.filter(task => filters.priorities.includes(task.priority));
    }

    // Filter by assignees
    if (filters.assignees.length > 0) {
      filtered = filtered.filter(task => 
        task.assignee && filters.assignees.includes(task.assignee._id)
      );
    }

    // Filter by statuses
    if (filters.statuses.length > 0) {
      filtered = filtered.filter(task => filters.statuses.includes(task.status));
    }

    // Filter by date presence
    if (filters.hasDate !== null) {
      if (filters.hasDate) {
        filtered = filtered.filter(task => task.startDate || task.deadline);
      } else {
        filtered = filtered.filter(task => !task.startDate && !task.deadline);
      }
    }

    return filtered;
  }, [tasks, filters]);

  // Calculate time range based on tasks
  const { start, end } = getTimeRange();
  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  // Calculate task position
  const getTaskPosition = (task: Task) => {
    // Try startDate first, then deadline, then return null if neither exists
    const startDate = task.startDate ? new Date(task.startDate) : (task.deadline ? new Date(task.deadline) : null);
    if (!startDate || isNaN(startDate.getTime())) {
      console.log('Task has no valid date:', task.title, { startDate: task.startDate, deadline: task.deadline });
      return null;
    }
    
    // Normalize dates to start of day for accurate calculation
    const taskStart = new Date(startDate);
    taskStart.setHours(0, 0, 0, 0);
    const rangeStart = new Date(start);
    rangeStart.setHours(0, 0, 0, 0);
    const rangeEnd = new Date(end);
    rangeEnd.setHours(23, 59, 59, 999);
    
    const daysDiff = Math.floor((taskStart.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24));
    const duration = task.duration || 1;
    const endDate = new Date(taskStart);
    endDate.setDate(endDate.getDate() + duration - 1);
    endDate.setHours(23, 59, 59, 999);
    const endDaysDiff = Math.floor((endDate.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24));
    
    // Allow tasks that are partially visible (start before range or end after range)
    // Task is visible if it overlaps with the range at all
    if (endDate < rangeStart || taskStart > rangeEnd) {
      console.log('Task outside range:', task.title, {
        taskStart: taskStart.toISOString(),
        taskEnd: endDate.toISOString(),
        rangeStart: rangeStart.toISOString(),
        rangeEnd: rangeEnd.toISOString()
      });
      return null;
    }
    
    // Calculate position - clamp to visible range
    const visibleStart = Math.max(0, daysDiff);
    const visibleEnd = Math.min(totalDays, endDaysDiff + 1);
    const visibleWidth = visibleEnd - visibleStart;
    
    if (visibleWidth <= 0) {
      console.log('Task has zero width:', task.title);
      return null;
    }
    
    const leftPercent = (visibleStart / totalDays) * 100;
    const widthPercent = (visibleWidth / totalDays) * 100;
    
    return {
      left: leftPercent,
      width: Math.max(widthPercent, 1), // Minimum 1% width
      startDate: taskStart,
      endDate: endDate
    };
  };

  const getTaskColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444'; // red-500
      case 'high': return '#a855f7'; // purple-500
      case 'medium': return '#eab308'; // yellow-500
      case 'low': return '#3b82f6'; // blue-500
      default: return '#6b7280'; // gray-500
    }
  };

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveTaskId(active.id as string);
  };

  // Handle drag over - calculate date from position
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || over.id.toString().startsWith('section-')) return;
    
    // Calculate date from mouse position
    const timelineElement = document.querySelector('.timeline-grid');
    if (!timelineElement) return;
    
    const rect = timelineElement.getBoundingClientRect();
    const x = event.activatorEvent instanceof MouseEvent ? event.activatorEvent.clientX - rect.left : 0;
    const percent = (x / rect.width) * 100;
    const daysFromStart = (percent / 100) * totalDays;
    const calculatedDate = new Date(start);
    calculatedDate.setDate(calculatedDate.getDate() + Math.floor(daysFromStart));
    
    setDraggedDate(calculatedDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }));
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTaskId(null);
    setDraggedDate(null);
    
    if (!over) return;
    
    const taskId = active.id as string;
    const task = tasks.find(t => t._id === taskId);
    if (!task) return;
    
    // Check if dropped on a section
    if (over.id.toString().startsWith('section-')) {
      const sectionId = over.id.toString().replace('section-', '');
      const section = sections.find(s => s.id === sectionId);
      if (section && section.status && section.status !== task.status) {
        // Update task status
        try {
          await axios.put(
            `${API_BASE}/tasks/${taskId}`,
            { status: section.status },
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
          );
          fetchTasks();
        } catch (err: any) {
          console.error('Erreur mise à jour statut:', err);
          alert(err.response?.data?.error || 'Error updating');
        }
      }
    } else {
      // Calculate new date from position
      const timelineElement = document.querySelector('.timeline-grid');
      if (!timelineElement) return;
      
      const rect = timelineElement.getBoundingClientRect();
      const x = event.activatorEvent instanceof MouseEvent ? event.activatorEvent.clientX - rect.left : 0;
      const percent = (x / rect.width) * 100;
      const daysFromStart = (percent / 100) * totalDays;
      const newDate = new Date(start);
      newDate.setDate(newDate.getDate() + Math.floor(daysFromStart));
      
      // Update task deadline/startDate
      try {
        const updateData: any = {};
        if (task.startDate) {
          updateData.startDate = newDate.toISOString().split('T')[0];
          // Keep duration, update deadline accordingly
          if (task.duration) {
            const newDeadline = new Date(newDate);
            newDeadline.setDate(newDeadline.getDate() + task.duration - 1);
            updateData.deadline = newDeadline.toISOString().split('T')[0];
          }
        } else {
          updateData.deadline = newDate.toISOString().split('T')[0];
        }
        
        await axios.put(
          `${API_BASE}/tasks/${taskId}`,
          updateData,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        fetchTasks();
      } catch (err: any) {
        console.error('Erreur mise à jour date:', err);
        alert(err.response?.data?.error || 'Error updating');
      }
    }
  };

  // Handle task click to select and show details
  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setActiveTaskId(task._id);
  };

  // Scroll to task in timeline
  const scrollToTask = (task: Task) => {
    // Also select the task
    setSelectedTask(task);
    setActiveTaskId(task._id);

    const taskDate = task.startDate ? new Date(task.startDate) : (task.deadline ? new Date(task.deadline) : null);
    if (!taskDate || isNaN(taskDate.getTime())) {
      console.log('Task has no valid date to scroll to:', task.title);
      return;
    }

    const timelineContainer = document.querySelector('.timeline-container');
    if (!timelineContainer) {
      console.log('Timeline container not found');
      return;
    }

    const { start } = getTimeRange();
    const daysDiff = Math.floor((taskDate.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const weekWidth = zoomLevel === 'quarter' ? 60 : zoomLevel === 'month' ? 80 : 100;
    const scrollPosition = (daysDiff / 7) * weekWidth - (timelineContainer.clientWidth / 2);
    
    timelineContainer.scrollTo({ 
      left: Math.max(0, scrollPosition), 
      behavior: 'smooth' 
    });

    // Highlight the task and scroll to it
    setTimeout(() => {
      const taskElement = document.querySelector(`[data-task-id="${task._id}"]`);
      if (taskElement) {
        taskElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      }
    }, 300);
  };

  // Handle save task duration
  const handleSaveTaskDuration = async () => {
    if (!selectedTask) return;
    
    try {
      const updateData: any = {
        duration: editTaskData.duration
      };
      
      if (editTaskData.startDate) {
        updateData.startDate = editTaskData.startDate;
        // Update deadline based on start date and duration
        const start = new Date(editTaskData.startDate);
        const deadline = new Date(start);
        deadline.setDate(deadline.getDate() + editTaskData.duration - 1);
        updateData.deadline = deadline.toISOString().split('T')[0];
      } else if (editTaskData.deadline) {
        updateData.deadline = editTaskData.deadline;
        // Calculate start date from deadline and duration
        const deadline = new Date(editTaskData.deadline);
        const start = new Date(deadline);
        start.setDate(start.getDate() - (editTaskData.duration - 1));
        updateData.startDate = start.toISOString().split('T')[0];
      }
      
      await axios.put(
        `${API_BASE}/tasks/${selectedTask._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setShowEditTaskModal(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (err: any) {
      console.error('Erreur mise à jour durée:', err);
      alert(err.response?.data?.error || 'Error updating');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
        <p className="text-gray-600 text-lg">Chargement de la timeline...</p>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <AlertTriangle className="h-16 w-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Erreur</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button
          onClick={() => navigate('/projects')}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retour aux projets
        </button>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-gray-600 text-lg">Project not found.</p>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-gray-50'}>
      {/* Shared Project Navbar */}
      {!embedded && (
        <ProjectNavbar
          project={project}
          projectId={projectId || ''}
          isCreator={isCreator}
        />
      )}

      {/* Timeline Specific Controls */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-full mx-auto px-4 py-2">
          <div className="flex items-center justify-between">
            {/* Left side - Title and view icons */}
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-semibold text-gray-900">Project Timeline</h2>
              <div className="flex items-center gap-0.5 border border-gray-300 rounded-md overflow-hidden">
                <button className="p-1.5 hover:bg-gray-50 transition-colors">
                  <List className="h-3.5 w-3.5 text-gray-600" />
                </button>
                <button className="p-1.5 bg-blue-50 border-l border-r border-gray-300 transition-colors">
                  <Grid3x3 className="h-3.5 w-3.5 text-blue-600" />
                </button>
                <button className="p-1.5 hover:bg-gray-50 transition-colors">
                  <Calendar className="h-3.5 w-3.5 text-gray-600" />
                </button>
              </div>
            </div>

            {/* Right side - Search, Settings, Add Task */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Q Search tasks..."
                  className="pl-8 pr-3 py-1.5 border border-gray-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 w-40"
                />
              </div>
              <button className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md transition-colors">
                <Settings className="h-4 w-4" />
              </button>
              <button
                onClick={() => setShowCreateTaskModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-xs font-medium"
              >
                <Plus className="h-3.5 w-3.5" />
                Add Task
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Content */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        <div className="flex h-[calc(100vh-80px)]">
          {/* Left Panel - STATUS / TASKS */}
          <div className="w-64 border-r border-gray-200 bg-white overflow-y-auto">
            <div className="p-4">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">STATUS / TASKS</h2>
              <div className="space-y-2">
                {sections.map((section) => {
                  const filteredTasks = getFilteredTasks();
                  const sectionTasks = filteredTasks.filter(task => {
                    if (section.status) {
                      return task.status === section.status;
                    }
                    return false;
                  });

                  return (
                    <DroppableSection
                      key={section.id}
                      section={section}
                      sectionTasks={sectionTasks}
                      onToggleCollapse={() => {
                        setSections(prev => prev.map(s => 
                          s.id === section.id ? { ...s, collapsed: !s.collapsed } : s
                        ));
                      }}
                      onTaskClick={scrollToTask}
                    />
                  );
                })}
              </div>
            </div>
          </div>

        {/* Right Panel - Timeline Grid */}
        <div className="flex-1 flex overflow-hidden">
          <div className="w-full overflow-x-auto bg-white timeline-grid timeline-container">
            <TimelineGrid
              tasks={getFilteredTasks()}
              zoomLevel={zoomLevel}
              currentDate={currentDate}
              sections={sections}
              activeTaskId={activeTaskId}
              draggedDate={draggedDate}
              onTaskClick={handleTaskClick}
              getTaskPosition={getTaskPosition}
              getTaskColor={getTaskColor}
              start={start}
              end={end}
              totalDays={totalDays}
            />
          </div>
        </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeTaskId && draggedDate && (
            <div className="bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg text-sm">
              {draggedDate}
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Task Details Drawer - Opens from right with dark overlay */}
      {selectedTask && (
        <>
          {/* Dark Overlay - Covers entire page including navbar */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[100] transition-opacity"
            onClick={() => setSelectedTask(null)}
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
          />
          
          {/* Drawer Panel */}
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl z-[101] transform transition-transform duration-300 ease-in-out overflow-y-auto">
            <TaskDetailsPanel
              task={selectedTask}
              project={project}
              onClose={() => setSelectedTask(null)}
              onUpdate={fetchTasks}
              getTaskPosition={getTaskPosition}
            />
          </div>
        </>
      )}

      {/* Filter Modal */}
      {showFilterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Filter Tasks</h3>
              <button
                onClick={() => setShowFilterModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Priority</label>
                <div className="space-y-2">
                  {['urgent', 'high', 'medium', 'low'].map((priority) => (
                    <label key={priority} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(priority)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({
                              ...prev,
                              priorities: [...prev.priorities, priority]
                            }));
                          } else {
                            setFilters(prev => ({
                              ...prev,
                              priorities: prev.priorities.filter(p => p !== priority)
                            }));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{priority}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Status</label>
                <div className="space-y-2">
                  {['todo', 'in-progress', 'review', 'done', 'blocked'].map((status) => (
                    <label key={status} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilters(prev => ({
                              ...prev,
                              statuses: [...prev.statuses, status]
                            }));
                          } else {
                            setFilters(prev => ({
                              ...prev,
                              statuses: prev.statuses.filter(s => s !== status)
                            }));
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">{status.replace('-', ' ')}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Assignee Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Assignee</label>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {Array.from(new Set(tasks.filter(t => t.assignee).map(t => t.assignee!._id)))
                    .map(assigneeId => {
                      const assignee = tasks.find(t => t.assignee?._id === assigneeId)?.assignee;
                      if (!assignee) return null;
                      return (
                        <label key={assigneeId} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filters.assignees.includes(assigneeId)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setFilters(prev => ({
                                  ...prev,
                                  assignees: [...prev.assignees, assigneeId]
                                }));
                              } else {
                                setFilters(prev => ({
                                  ...prev,
                                  assignees: prev.assignees.filter(a => a !== assigneeId)
                                }));
                              }
                            }}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{assignee.name}</span>
                        </label>
                      );
                    })}
                  {tasks.filter(t => t.assignee).length === 0 && (
                    <p className="text-sm text-gray-400">No assignees found</p>
                  )}
                </div>
              </div>

              {/* Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Has Date</label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasDate"
                      checked={filters.hasDate === true}
                      onChange={() => setFilters(prev => ({ ...prev, hasDate: true }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">With date</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasDate"
                      checked={filters.hasDate === false}
                      onChange={() => setFilters(prev => ({ ...prev, hasDate: false }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Without date</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="hasDate"
                      checked={filters.hasDate === null}
                      onChange={() => setFilters(prev => ({ ...prev, hasDate: null }))}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">All</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t px-6 pb-4">
              <button
                onClick={() => {
                  setFilters({
                    priorities: [],
                    assignees: [],
                    statuses: [],
                    hasDate: null,
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Clear all
              </button>
              <button
                onClick={() => setShowFilterModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Create Task</h3>
              <button
                onClick={() => {
                  setShowCreateTaskModal(false);
                  setTaskFormData({
                    title: '',
                    description: '',
                    status: 'todo',
                    priority: 'medium',
                    deadline: '',
                    startDate: '',
                    duration: 1,
                    assignee: '',
                    team: ''
                  });
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!taskFormData.team || !projectId) {
                  alert('Please select a team');
                  return;
                }
                try {
                  await axios.post(
                    `${API_BASE}/tasks/team/${taskFormData.team}`,
                    {
                      title: taskFormData.title,
                      description: taskFormData.description,
                      status: taskFormData.status,
                      priority: taskFormData.priority,
                      deadline: taskFormData.deadline || null,
                      assignee: taskFormData.assignee || null
                    },
                    {
                      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                    }
                  );
                  setShowCreateTaskModal(false);
                  setTaskFormData({
                    title: '',
                    description: '',
                    status: 'todo',
                    priority: 'medium',
                    deadline: '',
                    startDate: '',
                    duration: 1,
                    assignee: '',
                    team: ''
                  });
                  fetchTasks();
                } catch (err: any) {
                  console.error('Erreur création tâche:', err);
                  alert(err.response?.data?.error || 'Error creating item');
                }
              }}
              className="p-6 space-y-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  required
                  value={taskFormData.title}
                  onChange={(e) => setTaskFormData({ ...taskFormData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Task title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  value={taskFormData.description}
                  onChange={(e) => setTaskFormData({ ...taskFormData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Task description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Team *</label>
                  <select
                    required
                    value={taskFormData.team}
                    onChange={(e) => setTaskFormData({ ...taskFormData, team: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select a team</option>
                    {project?.teams?.map((team) => (
                      <option key={team._id} value={team._id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select
                    value={taskFormData.status}
                    onChange={(e) => setTaskFormData({ ...taskFormData, status: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="todo">To do</option>
                    <option value="in-progress">Doing</option>
                    <option value="review">Review</option>
                    <option value="done">Done</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                  <select
                    value={taskFormData.priority}
                    onChange={(e) => setTaskFormData({ ...taskFormData, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                  <input
                    type="date"
                    value={taskFormData.deadline}
                    onChange={(e) => setTaskFormData({ ...taskFormData, deadline: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
                <select
                  value={taskFormData.assignee}
                  onChange={(e) => setTaskFormData({ ...taskFormData, assignee: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Unassigned</option>
                  {project?.teams
                    ?.find((t) => t._id === taskFormData.team)
                    ?.members?.map((member: any) => (
                      <option key={member._id} value={member._id}>
                        {member.name || member.email}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateTaskModal(false);
                    setTaskFormData({
                      title: '',
                      description: '',
                      status: 'todo',
                      priority: 'medium',
                      deadline: '',
                      startDate: '',
                      duration: 1,
                      assignee: '',
                      team: ''
                    });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Task Details Panel Component
const TaskDetailsPanel: React.FC<{
  task: Task;
  project: ProjectData | null;
  onClose: () => void;
  onUpdate: () => void;
  getTaskPosition: (task: Task) => { left: number; width: number; startDate: Date; endDate: Date } | null;
}> = ({ task, project, onClose, onUpdate, getTaskPosition }) => {
  const API_BASE = API_BASE_URL;
  
  // Helper function to convert ISO date string to yyyy-MM-dd format for date inputs
  const formatDateForInput = (dateString: string | undefined): string => {
    if (!dateString) return '';
    try {
      // If already in yyyy-MM-dd format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // Otherwise, parse as ISO and convert
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const [editData, setEditData] = useState({
    title: task.title,
    description: task.description || '',
    assignee: task.assignee?._id || '',
    startDate: formatDateForInput(task.startDate),
    deadline: formatDateForInput(task.deadline),
    duration: task.duration || 1,
    priority: task.priority,
    status: task.status
  });

  const [isSaving, setIsSaving] = useState(false);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasChangesRef = useRef(false);

  // Update editData when task changes
  useEffect(() => {
    setEditData({
      title: task.title,
      description: task.description || '',
      assignee: task.assignee?._id || '',
      startDate: formatDateForInput(task.startDate),
      deadline: formatDateForInput(task.deadline),
      duration: task.duration || 1,
      priority: task.priority,
      status: task.status
    });
    hasChangesRef.current = false;
  }, [task._id]);

  const position = getTaskPosition(task);
  const startDate = position?.startDate || (task.startDate ? new Date(task.startDate) : null);
  const endDate = position?.endDate || (task.deadline ? new Date(task.deadline) : null);

  const handleSave = useCallback(async () => {
    if (!hasChangesRef.current) return;
    
    hasChangesRef.current = false;
    setIsSaving(true);
    try {
      const updateData: any = {
        title: editData.title,
        description: editData.description,
        priority: editData.priority,
        status: editData.status,
        assignee: editData.assignee || null,
        duration: editData.duration || 1
      };

      // Handle dates - always send both if they exist
      if (editData.startDate) {
        updateData.startDate = editData.startDate;
        // If we have startDate and duration, calculate deadline
        if (editData.duration) {
          const start = new Date(editData.startDate);
          const deadline = new Date(start);
          deadline.setDate(deadline.getDate() + editData.duration - 1);
          updateData.deadline = deadline.toISOString().split('T')[0];
        }
      }
      
      if (editData.deadline) {
        updateData.deadline = editData.deadline;
        // If we have deadline and duration but no startDate, calculate startDate
        if (editData.duration && !editData.startDate) {
          const deadline = new Date(editData.deadline);
          const start = new Date(deadline);
          start.setDate(start.getDate() - (editData.duration - 1));
          updateData.startDate = start.toISOString().split('T')[0];
        }
      }

      // If both dates exist, ensure they're consistent with duration
      if (editData.startDate && editData.deadline && editData.duration) {
        const start = new Date(editData.startDate);
        const calculatedDeadline = new Date(start);
        calculatedDeadline.setDate(calculatedDeadline.getDate() + editData.duration - 1);
        // Use the calculated deadline to ensure consistency
        updateData.deadline = calculatedDeadline.toISOString().split('T')[0];
      }

      console.log('Updating task with data:', updateData);

      const response = await axios.put(
        `${API_BASE}/tasks/${task._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      console.log('Task updated successfully:', response.data);
      onUpdate();
    } catch (err: any) {
      console.error('Erreur mise à jour:', err);
      console.error('Error details:', err.response?.data);
      alert(err.response?.data?.error || 'Error updating');
    } finally {
      setIsSaving(false);
    }
  }, [task._id, editData, onUpdate]);

  // Debounced save function
  const debouncedSave = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      handleSave();
    }, 1000); // Save after 1 second of inactivity
  }, [handleSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const getAllTeamMembers = () => {
    if (!project?.teams) return [];
    const membersMap = new Map();
    project.teams.forEach(team => {
      team.members?.forEach((member: any) => {
        membersMap.set(member._id, member);
      });
    });
    return Array.from(membersMap.values());
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700';
      case 'high': return 'bg-purple-100 text-purple-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'done': return 'bg-green-100 text-green-700';
      case 'in-progress': return 'bg-blue-100 text-blue-700';
      case 'review': return 'bg-yellow-100 text-yellow-700';
      case 'blocked': return 'bg-red-100 text-red-700';
      case 'todo': return 'bg-gray-100 text-gray-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="border-b border-gray-200 p-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Task Details</h2>
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Task Title */}
        <div>
          <input
            type="text"
            value={editData.title}
            onChange={(e) => {
              setEditData({ ...editData, title: e.target.value });
              hasChangesRef.current = true;
              debouncedSave();
            }}
            onBlur={handleSave}
            className="w-full text-xl font-semibold text-gray-900 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            placeholder="Task title"
          />
          {isSaving && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              Saving...
            </p>
          )}
        </div>

        {/* Assignee */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Assignee</label>
          <div className="flex items-center gap-3">
            {editData.assignee && getAllTeamMembers().find((m: any) => m._id === editData.assignee) ? (
              <div className="w-10 h-10 rounded-full bg-purple-500 text-white flex items-center justify-center font-medium flex-shrink-0">
                {getAllTeamMembers().find((m: any) => m._id === editData.assignee)?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            ) : null}
            <select
              value={editData.assignee}
              onChange={(e) => {
                setEditData({ ...editData, assignee: e.target.value });
                hasChangesRef.current = true;
                handleSave();
              }}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            >
              <option value="">Unassigned</option>
              {getAllTeamMembers().map((member: any) => (
                <option key={member._id} value={member._id}>
                  {member.name || member.email}
                </option>
              ))}
            </select>
            {editData.assignee && (
              <button
                onClick={async () => {
                  setEditData({ ...editData, assignee: '' });
                  hasChangesRef.current = true;
                  await handleSave();
                }}
                className="p-2 hover:bg-red-100 rounded-lg text-red-600 transition-colors"
                title="Remove assignee"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Dates Section - Enriched */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Dates</label>
          
          {/* Start Date */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Start Date</label>
            <input
              type="date"
              value={editData.startDate}
              onChange={(e) => {
                setEditData({ ...editData, startDate: e.target.value });
                hasChangesRef.current = true;
                debouncedSave();
              }}
              onBlur={handleSave}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            />
          </div>

          {/* Due Date */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Due Date</label>
            <input
              type="date"
              value={editData.deadline}
              onChange={(e) => {
                setEditData({ ...editData, deadline: e.target.value });
                hasChangesRef.current = true;
                debouncedSave();
              }}
              onBlur={handleSave}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            />
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Duration (days)
            </label>
            <input
              type="number"
              min="1"
              value={editData.duration}
              onChange={(e) => {
                setEditData({ ...editData, duration: parseInt(e.target.value) || 1 });
                hasChangesRef.current = true;
                debouncedSave();
              }}
              onBlur={handleSave}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            />
          </div>

          {/* Date Range Display */}
          {startDate && endDate && (
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-xs text-blue-700 font-medium mb-1">Timeline Range</p>
              <p className="text-sm text-blue-900">
                {startDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - {endDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>

        {/* Project */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Project</label>
          {project && (
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: project.color }}></div>
              <span className="text-sm text-gray-900 flex-1">{project.title}</span>
            </div>
          )}
        </div>

        {/* Dependencies */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Dependencies</label>
          <button className="w-full px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:bg-gray-50 flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Add dependencies
          </button>
        </div>

        {/* Fields */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Fields</label>
          
          {/* Priority */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Priority</label>
            <select
              value={editData.priority}
              onChange={(e) => {
                setEditData({ ...editData, priority: e.target.value as 'low' | 'medium' | 'high' | 'urgent' });
                hasChangesRef.current = true;
                handleSave();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              value={editData.status}
              onChange={(e) => {
                setEditData({ ...editData, status: e.target.value as 'todo' | 'in-progress' | 'review' | 'done' | 'blocked' | 'archived' });
                hasChangesRef.current = true;
                handleSave();
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors"
            >
              <option value="todo">To do</option>
              <option value="in-progress">In Progress</option>
              <option value="review">Review</option>
              <option value="done">Done</option>
              <option value="blocked">Blocked</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
          <textarea
            value={editData.description}
            onChange={(e) => {
              setEditData({ ...editData, description: e.target.value });
              hasChangesRef.current = true;
              debouncedSave();
            }}
            onBlur={handleSave}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors resize-none"
            placeholder="What is this task about?"
          />
        </div>
      </div>

      {/* Footer */}
      {isSaving && (
        <div className="border-t border-gray-200 p-4 flex items-center justify-center">
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Saving changes...</span>
          </div>
        </div>
      )}
    </div>
  );
};

// Droppable Section Component
const DroppableSection: React.FC<{
  section: { id: string; name: string; status?: string; collapsed: boolean };
  sectionTasks: Task[];
  onToggleCollapse: () => void;
  onAddSection?: () => void;
  onTaskClick?: (task: Task) => void;
}> = ({ section, sectionTasks, onToggleCollapse, onAddSection, onTaskClick }) => {
  const { setNodeRef, isOver } = useDroppable({
    id: `section-${section.id}`,
  });

  // Get task initial for avatar
  const getTaskInitial = (task: Task) => {
    if (task.assignee?.name) {
      return task.assignee.name[0].toUpperCase();
    }
    return task.title[0]?.toUpperCase() || '?';
  };

  // Get color for task initial
  const getTaskColor = (task: Task) => {
    const colors = ['bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-pink-500', 'bg-indigo-500'];
    const index = task.title.charCodeAt(0) % colors.length;
    return colors[index];
  };

  return (
    <div className="mb-2">
      <button
        onClick={onToggleCollapse}
        className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded transition-colors"
      >
        <div className="flex items-center gap-2">
          {section.collapsed ? (
            <ChevronRight className="h-3 w-3 text-gray-400" />
          ) : (
            <ChevronDownIcon className="h-3 w-3 text-gray-400" />
          )}
          <span className="text-sm font-medium text-gray-900">{section.name}</span>
          <span className="text-xs text-gray-500">({sectionTasks.length} task{sectionTasks.length !== 1 ? 's' : ''})</span>
        </div>
      </button>
      {!section.collapsed && (
        <div
          ref={setNodeRef}
          className={`mt-1 space-y-1 ${isOver ? 'bg-blue-50' : ''} rounded transition-colors`}
        >
          {sectionTasks.map((task) => (
            <div
              key={task._id}
              onClick={() => onTaskClick?.(task)}
              className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer transition-colors"
            >
              <div className={`w-6 h-6 rounded-full ${getTaskColor(task)} text-white text-xs flex items-center justify-center font-medium flex-shrink-0`}>
                {getTaskInitial(task)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-900 truncate">{task.title}</p>
                {task.deadline && (
                  <p className="text-xs text-gray-500">Due {new Date(task.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                )}
              </div>
            </div>
          ))}
          {sectionTasks.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-2">No tasks</p>
          )}
        </div>
      )}
    </div>
  );
};

// Draggable Task Component
const DraggableTask: React.FC<{
  task: Task;
  position: { left: number; width: number; startDate: Date; endDate: Date };
  color: string;
  isActive: boolean;
  onClick: () => void;
}> = ({ task, position, color, isActive, onClick }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: task._id,
  });

  const style = {
    width: '100%',
    minWidth: '40px', // Ensure minimum width for visibility
    height: '36px', // Fixed height
    backgroundColor: color,
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.6 : 1,
    border: isActive ? '2px solid #3b82f6' : 'none',
    boxShadow: isActive ? '0 2px 6px rgba(59, 130, 246, 0.4)' : '0 1px 2px rgba(0, 0, 0, 0.15)',
    zIndex: isActive ? 20 : 10,
  };

  // Truncate title for display
  const displayTitle = task.title.length > 15 ? task.title.substring(0, 15) + '...' : task.title;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className="absolute top-0 left-0 right-0 rounded px-1.5 py-1 text-[11px] font-medium text-white cursor-move hover:opacity-85 transition-opacity flex items-center h-full"
      style={style}
      onClick={onClick}
      title={`${task.title} - ${position.startDate.toLocaleDateString()} to ${position.endDate.toLocaleDateString()}`}
    >
      <span className="truncate flex-1">{displayTitle}</span>
    </div>
  );
};

// TimelineGrid Component
const TimelineGrid: React.FC<{
  tasks: Task[];
  zoomLevel: 'week' | 'month' | 'quarter';
  currentDate: Date;
  sections: { id: string; name: string; status?: string; collapsed: boolean }[];
  activeTaskId: string | null;
  draggedDate: string | null;
  onTaskClick: (task: Task) => void;
  getTaskPosition: (task: Task) => { left: number; width: number; startDate: Date; endDate: Date } | null;
  getTaskColor: (priority: string) => string;
  start: Date;
  end: Date;
  totalDays: number;
}> = ({ tasks, zoomLevel, currentDate, sections, activeTaskId, draggedDate, onTaskClick, getTaskPosition, getTaskColor, start, end, totalDays }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Use props for timeline range
  const timelineStart = new Date(start);
  timelineStart.setHours(0, 0, 0, 0);
  const timelineEnd = new Date(end);
  timelineEnd.setHours(23, 59, 59, 999);
  const timelineTotalDays = totalDays;

  // Generate hierarchical structure: months and weeks
  const getTimeStructure = () => {
    const months: { 
      label: string; 
      start: Date; 
      end: Date; 
      weeks: { label: string; weekNum: number; start: Date; end: Date }[] 
    }[] = [];
    
    // Start from the first day of the first month in the range
    const current = new Date(timelineStart);
    current.setDate(1); // Start from first day of month
    
    // End at the last day of the last month in the range
    const endMonth = new Date(timelineEnd);
    endMonth.setMonth(endMonth.getMonth() + 1);
    endMonth.setDate(0); // Last day of the last month
    
    while (current <= endMonth) {
      const monthStart = new Date(current.getFullYear(), current.getMonth(), 1);
      const monthEnd = new Date(current.getFullYear(), current.getMonth() + 1, 0);
      
      // Get actual month range within timeline (clamp to timeline bounds)
      const actualMonthStart = monthStart < timelineStart ? new Date(timelineStart) : new Date(monthStart);
      const actualMonthEnd = monthEnd > timelineEnd ? new Date(timelineEnd) : new Date(monthEnd);
      
      // Generate weeks for this month
      const weeks: { label: string; weekNum: number; start: Date; end: Date }[] = [];
      let weekCurrent = new Date(actualMonthStart);
      
      // Find the start of the week (Monday)
      const dayOfWeek = weekCurrent.getDay();
      const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      weekCurrent.setDate(weekCurrent.getDate() + diff);
      
      while (weekCurrent <= actualMonthEnd) {
        const weekStart = new Date(weekCurrent);
        const weekEnd = new Date(weekCurrent);
        weekEnd.setDate(weekEnd.getDate() + 6);
        
        // Only include weeks that overlap with the month
        if (weekStart <= actualMonthEnd && weekEnd >= actualMonthStart) {
          const weekNum = getWeekNumber(weekStart);
          const weekStartDay = weekStart.getDate();
          const weekStartMonth = weekStart.getMonth();
          const weekEndDay = weekEnd.getDate();
          const weekEndMonth = weekEnd.getMonth();
          
          let dateRange = '';
          if (weekStartMonth === weekEndMonth) {
            dateRange = `${weekStartDay} - ${weekEndDay}`;
          } else {
            dateRange = `${weekStartDay} ${weekStart.toLocaleDateString('en-US', { month: 'short' })} - ${weekEndDay}`;
          }
          
          weeks.push({
            label: `W${weekNum} ${dateRange}`,
            weekNum,
            start: new Date(weekStart),
            end: new Date(weekEnd)
          });
        }
        
        weekCurrent.setDate(weekCurrent.getDate() + 7);
      }
      
      if (weeks.length > 0) {
        months.push({
          label: monthStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          start: actualMonthStart,
          end: actualMonthEnd,
          weeks
        });
      }
      
      current.setMonth(current.getMonth() + 1);
    }
    
    return months;
  };

  const getWeekNumber = (date: Date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  };

  const timeStructure = getTimeStructure();
  // Adaptive width based on zoom level
  const weekWidth = zoomLevel === 'quarter' ? 60 : zoomLevel === 'month' ? 80 : 100;
  const allWeeks = timeStructure.flatMap(month => month.weeks);
  const totalWidth = allWeeks.length * weekWidth;

  return (
    <div className="relative h-full bg-gray-50">
      {/* Hierarchical Timeline Header */}
      <div className="sticky top-0 bg-white border-b-2 border-gray-300 z-10 shadow-md">
        {/* Month Row - Centered */}
        <div className="flex border-b border-gray-200">
          {timeStructure.map((month, monthIndex) => {
            const monthWeeks = month.weeks;
            const monthWidth = monthWeeks.length * weekWidth;
            
            return (
              <div
                key={`month-${monthIndex}`}
                className="border-r border-gray-300 px-3 py-2 font-semibold text-sm text-gray-800 bg-gray-50 flex items-center justify-center"
                style={{ 
                  width: `${monthWidth}px`, 
                  minWidth: `${monthWidth}px`,
                  flexShrink: 0
                }}
              >
                {month.label}
              </div>
            );
          })}
        </div>
        
        {/* Week Row */}
        <div className="flex bg-white">
          {allWeeks.map((week, weekIndex) => {
            // Extract week number and date range
            const weekParts = week.label.split(' ');
            const weekNum = weekParts[0]; // W47, W48, etc.
            const dateRange = weekParts.slice(1).join(' '); // "17 - 23", "24 - 30", etc.
            
            return (
              <div
                key={`week-${weekIndex}`}
                className="border-r border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-700 text-center"
                style={{ 
                  width: `${weekWidth}px`, 
                  minWidth: `${weekWidth}px`,
                  flexShrink: 0
                }}
              >
                <div className="font-semibold text-gray-900">{weekNum}</div>
                <div className="text-gray-500 text-[10px] mt-0.5 leading-tight">{dateRange}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Timeline Grid */}
      <div 
        className="relative bg-white" 
        style={{ 
          minHeight: `${sections.length * 60}px`,
          minWidth: `${totalWidth}px`
        }}
      >
        {/* Grid lines - vertical week separators */}
        <div className="absolute inset-0 pointer-events-none">
          {allWeeks.map((week, index) => (
            <div
              key={`grid-${index}`}
              className="absolute top-0 bottom-0 border-r border-gray-200"
              style={{ left: `${index * weekWidth}px` }}
            />
          ))}
        </div>

        {/* Today Indicator */}
        {today >= timelineStart && today <= timelineEnd && (
          <div
            className="today-indicator absolute top-0 bottom-0 w-0.5 bg-orange-500 z-20 pointer-events-none"
            style={{
              left: `${((today.getTime() - timelineStart.getTime()) / (timelineEnd.getTime() - timelineStart.getTime())) * 100}%`
            }}
          >
            <div className="absolute -top-1 -left-1.5 w-3 h-3 rounded-full bg-orange-500 border-2 border-white shadow-md"></div>
          </div>
        )}

        {/* Sections and Tasks */}
        {sections.map((section, sectionIndex) => {
          const sectionTasks = tasks.filter(task => {
            if (section.status) {
              return task.status === section.status;
            }
            return false;
          });

          // Only show section if it has tasks or is a main status (todo, in-progress, done)
          const isMainSection = ['todo', 'in-progress', 'done'].includes(section.status || '');
          if (!isMainSection && sectionTasks.length === 0) {
            return null;
          }

          return (
            <div
              key={section.id}
              className="border-b border-gray-300 relative"
              style={{ height: '60px', minHeight: '60px' }}
            >
              {/* Horizontal grid lines */}
              <div className="absolute inset-0 border-b border-gray-200"></div>
              
              {/* Task Blocks */}
              {sectionTasks.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                  No tasks in {section.name}
                </div>
              ) : (
                sectionTasks.map((task) => {
                  const position = getTaskPosition(task);
                  if (!position) {
                    // Log why task is not displayed
                    if (!task.startDate && !task.deadline) {
                      console.log(`Task "${task.title}" has no date`);
                    } else {
                      console.log(`Task "${task.title}" position is null`, {
                        startDate: task.startDate,
                        deadline: task.deadline,
                        duration: task.duration
                      });
                    }
                    return null;
                  }

                  console.log(`Rendering task "${task.title}" at position:`, position);

                  return (
                    <div
                      key={task._id}
                      data-task-id={task._id}
                      className="absolute"
                      style={{
                        left: `${position.left}%`,
                        width: `${Math.max(position.width, 2)}%`,
                        top: '12px',
                        height: '36px'
                      }}
                    >
                      <DraggableTask
                        task={task}
                        position={position}
                        color={getTaskColor(task.priority)}
                        isActive={activeTaskId === task._id}
                        onClick={() => onTaskClick(task)}
                      />
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimelinePage;

